import { useState } from 'react';

import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  IconButton,
  Label,
  Modal,
  Select,
  Text,
} from '@zillow/constellation-10';
import { IconCopyFilled, IconMinusCircleFilled, IconPlusFilled } from '@zillow/constellation-icons';

import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { useMultiplexUnitCreation } from '../../../../hooks';

import { MAX_NUM_MULTIPLEX_UNITS_VALUE } from '../../../../constants/AddPropertyConstants';
import { bathOptions, bedOptions } from '../../../../constants/ListingDetailsConstants';

import type { UserPropertyMultiplexCreateUnitRequestWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { CreateMultiplexResult } from '../../../../hooks';

const MAX_UNIT_NUMBER_LENGTH = 20;

type UnitRow = {
  id: string;
  unitNumber: string;
  beds: string;
  baths: string;
  size: string;
};

type Props = {
  isOpen: boolean;
  fullAddress: string;
  numUnits?: string;
  customerId?: string;
  onClose: () => void;
  onConfirm: (result: CreateMultiplexResult) => void;
};

const GRID_COLUMNS = 'minmax(0, 1fr) 120px 120px 140px 48px 48px';

const createEmptyRow = (): UnitRow => ({
  id: crypto.randomUUID(),
  unitNumber: '',
  beds: '',
  baths: '',
  size: '',
});

const DescriptionBox = () => (
  <FormHelp aria-hidden="true" css={{ visibility: 'hidden' }}>
    placeholder
  </FormHelp>
);

const isDuplicateUnitNumber = (
  id: string,
  unitNumber: string,
  rows: Array<UnitRow>,
  existingUnitNumbers: Array<string>,
): boolean => {
  const trimmed = unitNumber.trim();
  if (!trimmed) return false;
  return (
    rows.some((row) => row.id !== id && row.unitNumber.trim() === trimmed) || existingUnitNumbers.includes(trimmed)
  );
};

const UnitEntryStep = ({ isOpen, fullAddress, numUnits, customerId, onClose, onConfirm }: Props): JSX.Element => {
  const [rows, setRows] = useState<Array<UnitRow>>(() => {
    const count = numUnits ? parseInt(numUnits, 10) : 1;
    const safeCount = Number.isNaN(count) || count < 1 ? 1 : Math.min(count, MAX_NUM_MULTIPLEX_UNITS_VALUE);
    return Array.from({ length: safeCount }, createEmptyRow);
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [focusedUnitRowId, setFocusedUnitRowId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { existingUnitNumbers, isPending, createMultiplex } = useMultiplexUnitCreation({ fullAddress, customerId });

  const handleAddRow = () => {
    if (rows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE) return;
    setHasSubmitted(false);
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = (id: string) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleCopyRow = (id: string) => {
    if (rows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE) return;
    const source = rows.find((row) => row.id === id);
    if (!source) return;
    const copy: UnitRow = { ...source, id: crypto.randomUUID() };
    setRows((prev) => {
      const index = prev.findIndex((row) => row.id === id);
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  const handleFieldChange = (id: string, field: keyof Omit<UnitRow, 'id'>, value: string) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const hasErrors = rows.some(
      (row) =>
        !row.unitNumber.trim() ||
        row.unitNumber.length > MAX_UNIT_NUMBER_LENGTH ||
        isDuplicateUnitNumber(row.id, row.unitNumber, rows, existingUnitNumbers) ||
        !row.beds ||
        !row.baths,
    );
    if (hasErrors) return;

    const units: Array<UserPropertyMultiplexCreateUnitRequestWebDto> = rows.map((row) => ({
      unitNumber: row.unitNumber,
      bedroomCount: row.beds === 'studio' ? 0 : row.beds ? Number(row.beds) : undefined,
      bathroomCount: row.baths ? Number(row.baths) : undefined,
      squareFeet: row.size ? Number(row.size) : undefined,
    }));

    createMultiplex(units, {
      onSuccess: (result) => onConfirm(result),
      onError: (msg: string) => setErrorMessage(msg),
    });
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'xl' }}
      header={<Heading level={2}>Add property</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <Heading level={3} textStyle="heading-xs">
              Tell us about your units
            </Heading>
            <Text>
              Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your
              perfect renter.
            </Text>
          </Box>

          {existingUnitNumbers.length > 0 &&
            (() => {
              const unitLabel = existingUnitNumbers.length === 1 ? 'Unit' : 'Units';
              const unitListString =
                existingUnitNumbers.length > 1
                  ? `${existingUnitNumbers.slice(0, -1).join(', ')} and ${existingUnitNumbers[existingUnitNumbers.length - 1]}`
                  : existingUnitNumbers[0];
              const verb = existingUnitNumbers.length === 1 ? 'exists' : 'exist';
              return (
                <Alert role="status" tone="info">
                  {unitLabel} <strong>{unitListString}</strong> already {verb} at this property
                </Alert>
              );
            })()}

          {errorMessage && (
            <Alert role="status" tone="critical">
              {errorMessage}
            </Alert>
          )}

          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
            <Box
              css={{
                display: { base: 'none', md: 'grid' },
                gridTemplateColumns: GRID_COLUMNS,
                columnGap: 'default',
                alignItems: 'end',
              }}
            >
              <Label aria-hidden="true" required>
                Unit number
              </Label>
              <Label aria-hidden="true" required>
                Beds
              </Label>
              <Label aria-hidden="true" required>
                Baths
              </Label>
              <Label aria-hidden="true" optional>
                Size
              </Label>
              <Box aria-hidden="true" css={{ gridColumn: 'span 2' }}>
                <Label>Options</Label>
              </Box>
            </Box>

            {rows.map((row) => {
              const isFocused = focusedUnitRowId === row.id;
              const isOverLimit = row.unitNumber.length > MAX_UNIT_NUMBER_LENGTH;
              const isDuplicate = isDuplicateUnitNumber(row.id, row.unitNumber, rows, existingUnitNumbers);
              const isEmpty = hasSubmitted && !row.unitNumber.trim();
              const hasUnitNumberError = isEmpty || isDuplicate || isOverLimit;

              const showBedsError = hasSubmitted && !row.beds;
              const showBathsError = hasSubmitted && !row.baths;

              const unitNumberDescription = (() => {
                if (isEmpty) return <FormHelp>Unit number cannot be empty</FormHelp>;
                if (isDuplicate) return <FormHelp>This unit already exists</FormHelp>;
                if (isFocused || isOverLimit) {
                  return (
                    <CharacterCountFormHelp
                      currentLength={row.unitNumber.length}
                      maxLength={MAX_UNIT_NUMBER_LENGTH}
                      isFocused={isFocused}
                      label="Unit number"
                      helperText="Enter unit number (e.g., 1A, 201)"
                    />
                  );
                }
                return <DescriptionBox />;
              })();

              return (
                <Box
                  key={row.id}
                  data-testid="unit-row"
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr 1fr', md: GRID_COLUMNS },
                    columnGap: 'default',
                    alignItems: 'start',
                  }}
                >
                  <Box css={{ gridColumn: { base: '1 / span 2', md: 'auto' } }}>
                    <FormField
                      required={true}
                      error={hasUnitNumberError}
                      label={<Label css={{ md: { srOnly: true } }}>Unit number</Label>}
                      control={
                        <AdornedInput
                          startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                          input={
                            <AdornedInput.Input
                              value={row.unitNumber}
                              onChange={(e) => handleFieldChange(row.id, 'unitNumber', e.target.value)}
                              onFocus={() => setFocusedUnitRowId(row.id)}
                              onBlur={() => setFocusedUnitRowId(null)}
                            />
                          }
                        />
                      }
                      description={unitNumberDescription}
                    />
                  </Box>
                  <Box>
                    <FormField
                      required={true}
                      error={showBedsError}
                      label={<Label css={{ md: { srOnly: true } }}>Beds</Label>}
                      control={
                        <Select value={row.beds} onChange={(e) => handleFieldChange(row.id, 'beds', e.target.value)}>
                          {bedOptions.map((opt: string) => (
                            <option key={opt} value={opt}>
                              {opt || 'Select'}
                            </option>
                          ))}
                        </Select>
                      }
                      description={showBedsError ? <FormHelp>Number of beds is required</FormHelp> : <DescriptionBox />}
                    />
                  </Box>
                  <Box>
                    <FormField
                      required={true}
                      error={showBathsError}
                      label={<Label css={{ md: { srOnly: true } }}>Baths</Label>}
                      control={
                        <Select value={row.baths} onChange={(e) => handleFieldChange(row.id, 'baths', e.target.value)}>
                          {bathOptions.map((opt: string) => (
                            <option key={opt} value={opt}>
                              {opt || 'Select'}
                            </option>
                          ))}
                        </Select>
                      }
                      description={
                        showBathsError ? <FormHelp>Number of bathrooms is required</FormHelp> : <DescriptionBox />
                      }
                    />
                  </Box>
                  <Box>
                    <FormField
                      optional={true}
                      label={<Label css={{ md: { srOnly: true } }}>Size</Label>}
                      control={
                        <AdornedInput
                          input={
                            <AdornedInput.Input
                              type="text"
                              inputMode="numeric"
                              value={row.size}
                              onChange={(e) => handleFieldChange(row.id, 'size', e.target.value)}
                            />
                          }
                          endAdornment={<AdornedInput.Adornment>sqft</AdornedInput.Adornment>}
                        />
                      }
                      description={<DescriptionBox />}
                    />
                  </Box>
                  <Box
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignSelf: 'flex-start',
                      gridColumn: { base: 'auto', md: 'span 2' },
                    }}
                  >
                    <Box css={{ display: { base: 'block', md: 'none' }, marginBottom: 'tighter' }}>
                      <Text textStyle="body-sm-bold">Options</Text>
                    </Box>
                    <ButtonGroup aria-label="Unit options">
                      <IconButton
                        data-testid="copy-unit-button"
                        title="Copy unit"
                        size="md"
                        tone="neutral"
                        emphasis="bare"
                        icon={<IconCopyFilled />}
                        disabled={rows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE}
                        onClick={() => handleCopyRow(row.id)}
                      />
                      <IconButton
                        data-testid="remove-unit-button"
                        title="Remove unit"
                        size="md"
                        tone="critical"
                        emphasis="bare"
                        icon={<IconMinusCircleFilled />}
                        disabled={rows.length <= 1}
                        onClick={() => handleRemoveRow(row.id)}
                      />
                    </ButtonGroup>
                  </Box>
                </Box>
              );
            })}

            <Button
              data-testid="add-unit-button"
              fluid={false}
              tone="brand"
              emphasis="outlined"
              icon={<IconPlusFilled />}
              disabled={rows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE}
              onClick={handleAddRow}
              css={{ alignSelf: 'flex-start' }}
            >
              Add unit
            </Button>
          </Box>
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Unit entry actions">
          <Button fluid={false} tone="brand" emphasis="bare" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="add-property-button"
            fluid={false}
            tone="brand"
            emphasis="filled"
            loading={isPending}
            onClick={handleSubmit}
          >
            Add property
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default UnitEntryStep;
